import type { WatchlistAccount } from '@interfaces/watchList';
import {
  type DataTableTanstackTable,
  DEFAULT_TABLE_ROWS_PER_PAGE,
  useDataTable,
} from '@ovComponents/2-component/dataTable';
import { useMemo } from 'react';
import { MOCK_WATCH_LIST } from './watchList.mocks';
import { useWatchListColumns } from './watchListColumns';

/**
 * Percentage of the drift band consumed at which an account enters the Watch List.
 * Exported so the header and footer copy use the same constant.
 * When product configures a per-org threshold, convert this to a hook input.
 */
export const WATCH_LIST_APPROACH_THRESHOLD = 75;

export type UseWatchListReturn = {
  table: DataTableTanstackTable<WatchlistAccount>;
  approachingCount: number;
  totalRowCount: number;
  isLoading: boolean;
  error: Error | null;
};

/**
 * Central hook for the Watch List section: loads data, defensively filters,
 * derives the approaching-account count, and returns a configured TanStack
 * table instance (view-only — no navigation handler).
 *
 * @remarks
 * Currently backed by `MOCK_WATCH_LIST`. Swap to
 * `useQuery(FETCH_WATCH_LIST, ...)` without changing the returned shape
 * when the backend endpoint is available.
 */
export function useWatchList(): UseWatchListReturn {
  const columns = useWatchListColumns();

  // Source data — replace with useQuery when the backend is ready
  const allAccounts: WatchlistAccount[] = MOCK_WATCH_LIST;

  const filteredAccounts = useMemo(
    () =>
      allAccounts.filter(
        (account) =>
          account.bandConsumedPercentage >= WATCH_LIST_APPROACH_THRESHOLD &&
          !account.hasBreached,
      ),
    [],
  );

  const approachingCount = filteredAccounts.length;

  const { table } = useDataTable<WatchlistAccount>({
    data: filteredAccounts,
    columns,
    sortingMode: 'client',
    paginationMode: 'client',
    initialSorting: [{ id: 'bandConsumed', desc: true }],
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE,
  });

  return {
    table,
    approachingCount,
    totalRowCount: approachingCount,
    isLoading: false,
    error: null,
  };
}
